import { useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import TextField from '../../text-field/text-field';
import { ValueEditorProps } from 'react-querybuilder';
import '../filter.scss';
import DatePicker from '../../date-picker/date-picker';
import dayjs from 'dayjs';

import SearchableSelect from '../../searchable-select';
import _ from 'lodash';
import { formatLabel } from '../../../utils/format-text';
import { getToken } from '@/utils';
import { getV1FilterFilterTable } from '../../../api-client/api.system-feature/api';

const getFilterTableFields = async (
	tableName: string,
	searchText: string,
	limit: number,
	offset: number = 0
) => {
	try {
		const tokenObj = getToken();
		const response = await getV1FilterFilterTable({
			table: tableName,
			search: searchText,
			limit: limit,
			offset: offset,
			'x-token': tokenObj['x-token'],
			'x-timezone': String(tokenObj['x-timezone'])
		});

		// response could be of several types, but use optional chaining and type narrowing
		if ('data' in response && response.data) {
			const result: any[] = (response.data as any)?.result ?? [];
			const totalCount: number = (response as any)?.pagination?.totalCount ?? result.length;
			const items = result.map((r: any) => ({ label: r.name, value: r.id }));
			return { items, totalCount };
		}
		return { items: [], totalCount: 0 };
	} catch {
		return { items: [], totalCount: 0 };
	}
};

const ValueEditor = (props: ValueEditorProps) => {
	// eslint-disable-next-line
	let { values, value, handleOnChange, fieldData, rule, ...rest } = props;

	if (!value && fieldData.inputType == 'select') {
		value = null;
	} else if (!Array.isArray(value) && fieldData.inputType == 'select') {
		value = [];
	}

	const isDisabled = useMemo(() => {
		return rule.operator === 'null' || rule.operator === 'notNull';
	}, [rule.operator]);

	const customFetch = useCallback(
		async (params: any) => {
			const search = params?.search || '';
			const limit = params?.limit || 25;
			const offset = params?.skip || 0;
			if (fieldData.table) {
				const data = await getFilterTableFields(fieldData.table, search, limit, offset);
				const optionData = data.items.map((r: any) => ({ id: r.value, value: r.value, label: r.label }));
				return { data: optionData, pagination: { totalCount: data.totalCount } };
			} else {
				// const searchedOptions =
				// 	values?.filter((opt) => opt.label?.toLowerCase().includes(search.toLowerCase())) ||
				// 	[];
				const optionData = values?.map((item: any) => ({
					...item,
					value: item?.name,
					label: item.label ? formatLabel(item.label) : ''
				}));
				return {
					data: optionData,
					pagination: { totalCount: optionData.length }
				};
			}
		},
		[fieldData.table, values]
	);

	const renderOptions = () => {
		switch (fieldData.inputType) {
			case 'select':
				return (
					<SearchableSelect
						key={fieldData.name}
						label=''
						name='insurance_account'
						placeholder='Select'
						onChange={(e: any, selected: any) => {
							const mappedValue = selected?.map((i: any) => ({
								...i,
								name: i?.value
							}))
							handleOnChange(mappedValue)
						}}
						value={value?.map(i => i?.value)}
						multiple={true}
						customFetchApi={customFetch}
						disabled={isDisabled}
						onChipRemove={() => handleOnChange([])}
					/>
				);
			case 'date':
				return (
					<DatePicker
						onChange={(val) =>
							handleOnChange(
								dayjs(val).isValid() ? dayjs(val).format('YYYY-MM-DD') : ''
							)
						}
						value={value ? dayjs(value) : null}
						disabled={isDisabled}
						slotProps={{
							textField: {
								placeholder: 'YYYY-MM-DD',
								className: 'select-drps'
							}
						}}
						format='YYYY-MM-DD'
					/>
				);

			default:
				return (
					<TextField
						value={value}
						onChange={(e: any) => {
							handleOnChange(e.target.value);
						}}
						{...rest}
						inputProps={{
							style: {
								fontSize: '0.8125rem'
							}
						}}
						className='select-drps'
						type={fieldData.inputType}
						disabled={isDisabled}
					/>
				);
		}
	};

	return <Box width='100%'>{renderOptions()}</Box>;
};

export { ValueEditor };
export default ValueEditor;
